// ---------------------------------------------------------------------------
// Test Mode — departments (CLE-DASH).
//
// Mirrors the backend migration, which seeded departments from the distinct
// Person.department names. Ids are stable slugs so people/tasks test data can
// reference them.
// ---------------------------------------------------------------------------

import type { Department } from '../lib/departmentsApi';
import { isoDateTime } from './company';

interface DepartmentSeed {
  id: string;
  name: string;
  color: string;
}

export const DEPARTMENT_SEEDS: DepartmentSeed[] = [
  { id: 'dept-management', name: 'Management', color: '#a855f7' },
  { id: 'dept-engineering', name: 'Engineering', color: '#3b82f6' },
  { id: 'dept-delivery', name: 'Delivery', color: '#22c55e' },
  { id: 'dept-quality', name: 'Quality', color: '#eab308' },
  { id: 'dept-design', name: 'Design', color: '#f97316' },
  { id: 'dept-infrastructure', name: 'Infrastructure', color: '#64748b' },
  { id: 'dept-marketing', name: 'Marketing', color: '#ef4444' },
  { id: 'dept-operations', name: 'Operations', color: '#16a34a' },
  { id: 'dept-finance', name: 'Finance', color: '#6b7280' },
];

export function departmentIdForName(name: string): string | null {
  return DEPARTMENT_SEEDS.find((d) => d.name === name)?.id ?? null;
}

/** Departments with people/task counts filled in (GET /api/departments). */
export function buildDepartments(
  peopleDepartmentIds: (string | null)[],
  taskDepartmentIds: (string | null)[] = [],
): Department[] {
  const peopleCounts = new Map<string, number>();
  for (const id of peopleDepartmentIds) {
    if (id) peopleCounts.set(id, (peopleCounts.get(id) ?? 0) + 1);
  }
  const taskCounts = new Map<string, number>();
  for (const id of taskDepartmentIds) {
    if (id) taskCounts.set(id, (taskCounts.get(id) ?? 0) + 1);
  }
  return DEPARTMENT_SEEDS.map((d) => ({
    id: d.id,
    name: d.name,
    color: d.color,
    peopleCount: peopleCounts.get(d.id) ?? 0,
    taskCount: taskCounts.get(d.id) ?? 0,
    createdAt: isoDateTime(-540, 9),
    updatedAt: isoDateTime(-2, 14),
  }));
}
